import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleMultiSelect from '~/components/inputs/Select/SimpleMultiSelect';
import { DocumentType } from '~/types/document';

type NameType<T> = T extends string ? string : T extends {} ? keyof T : string;

interface DocumentTypeFilterProps<Filters extends string | {}> {
  className?: string;
  label?: string;
  placeholder?: string;
  name: NameType<Filters>;
  onChange: (name: NameType<Filters>, value: DocumentType[]) => void;
  value: DocumentType[];
  hideLabel?: boolean;
  dataTestId?: string;
  clearable?: boolean;
  hiddenTypes?: DocumentType[];
  visibleTypes?: DocumentType[];
}

const DefaultOptions: {
  labelKey: 'bill' | 'receipt' | 'invoice' | 'credit_note' | 'debit_note' | 'parcel' | 'self_invoice' | 'other';
  value: DocumentType;
}[] = [
  {
    labelKey: 'bill',
    value: DocumentType.Bill,
  },
  {
    labelKey: 'receipt',
    value: DocumentType.Receipt,
  },
  {
    labelKey: 'invoice',
    value: DocumentType.Invoice,
  },
  {
    labelKey: 'credit_note',
    value: DocumentType.CreditNote,
  },
  {
    labelKey: 'debit_note',
    value: DocumentType.DebitNote,
  },
  {
    labelKey: 'parcel',
    value: DocumentType.Parcel,
  },
  {
    labelKey: 'self_invoice',
    value: DocumentType.SelfInvoice,
  },
  { labelKey: 'other', value: DocumentType.Other },
];

const DocumentTypeFilter = <Filters extends string | {}>({
  className,
  label,
  placeholder,
  name,
  onChange,
  value,
  hideLabel,
  dataTestId,
  clearable = true,
  hiddenTypes = [],
  visibleTypes,
}: DocumentTypeFilterProps<Filters>) => {
  const { t } = useTranslation('inputs', { keyPrefix: 'document_type_filters' });

  const options = useMemo(() => {
    const baseOptions = visibleTypes
      ? visibleTypes
          .map((type) => DefaultOptions.find((option) => option.value === type))
          .filter((option): option is (typeof DefaultOptions)[number] => option !== undefined)
      : DefaultOptions;

    return baseOptions
      .filter((option) => hiddenTypes.includes(option.value) === false)
      .map((option) => ({
        label: t(`options.${option.labelKey}`),
        value: option.value,
      }));
  }, [hiddenTypes, t, visibleTypes]);

  return (
    <SimpleMultiSelect
      className={className}
      label={label ?? t('label')}
      placeholder={placeholder ?? label ?? t('label')}
      name={name}
      clearable={clearable}
      value={value}
      hideLabel={hideLabel}
      dataTestId={dataTestId}
      onChange={(val) => onChange(name, val as DocumentType[])}
      options={options}
      renderMultipleSelectionValue={(selected) => t('multiple_value', { count: selected.length })}
    />
  );
};

export default DocumentTypeFilter;
